import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Icon } from "@iconify/react/dist/iconify.js";
import MasterLayout from "../../masterLayout/MasterLayout";
import Breadcrumb from "../Breadcrumb";
import DengerAlert from "../child/Alert/DengerAlert";
import { handleUnauthorized } from "../../utils/apiHelper";
import { formatApiDate } from "../../utils/dateHelper";
import { fetchPromoterJoinedUsers } from "../../services/promoterRequestService";

const PromoterJoinedUsersList = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [searchQuery, setSearchQuery] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [sortColumn, setSortColumn] = useState("joinDate");
  const [sortOrder, setSortOrder] = useState("desc");

  const colSpan = 4;

  const formatDate = (dateStr) => formatApiDate(dateStr);

  const fetchList = async () => {
    if (!id) return;

    const formattedStartDate = startDate
      ? moment(startDate).format("YYYY-MM-DD")
      : "";
    const formattedEndDate = endDate
      ? moment(endDate).format("YYYY-MM-DD")
      : "";

    if (
      formattedStartDate &&
      formattedEndDate &&
      moment(formattedStartDate).isAfter(moment(formattedEndDate))
    ) {
      setErrorMessage("Start date cannot be greater than end date");
      setTimeout(() => setErrorMessage(""), 3000);
      return;
    }

    try {
      setLoading(true);
      setErrorMessage("");

      const response = await fetchPromoterJoinedUsers(id, {
        page: currentPage,
        limit: itemsPerPage,
        search_keyword: searchQuery || "",
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        sortBy: sortColumn,
        sortOrder,
      });

      const payload = response?.data || {};
      if (payload.status) {
        setItems(payload.data || []);
        setTotalItems(payload.pagination?.total || 0);
        setTotalPages(payload.pagination?.totalPages || 1);
      } else {
        setItems([]);
        setTotalItems(0);
        setTotalPages(1);
        setErrorMessage(
          payload.message || "Unable to fetch joined user list."
        );
      }
    } catch (error) {
      if (error?.response?.status === 401) {
        handleUnauthorized(navigate);
        return;
      }
      setItems([]);
      setTotalItems(0);
      setTotalPages(1);
      setErrorMessage(
        error?.response?.data?.message ||
          "Something went wrong while fetching joined users."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
  }, [
    id,
    currentPage,
    itemsPerPage,
    searchQuery,
    startDate,
    endDate,
    sortColumn,
    sortOrder,
  ]);

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortOrder("desc");
    }
    setCurrentPage(1);
  };

  const renderSortIcons = (column) => (
    <span className="sort-colume">
      <Icon
        icon="mdi:arrow-up"
        className={`arrow ${
          sortColumn === column && sortOrder === "asc" ? "active" : ""
        }`}
      />
      <Icon
        icon="mdi:arrow-down"
        className={`arrow ${
          sortColumn === column && sortOrder === "desc" ? "active" : ""
        }`}
      />
    </span>
  );

  const getPaginationRange = () => {
    const maxVisiblePages = 5;
    const pages = [];

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      let startPage = Math.max(1, currentPage - 2);
      let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
      if (endPage - startPage < maxVisiblePages - 1) {
        startPage = Math.max(1, endPage - maxVisiblePages + 1);
      }
      for (let i = startPage; i <= endPage; i++) pages.push(i);
    }

    return pages;
  };

  return (
    <MasterLayout>
      <Breadcrumb
        title2="Promoter Management"
        link2="/promoter"
        title3="View Promoter"
        link3={`/promoter/view-promoter/${id}`}
        title="Joined Users"
      />

      <div className="card h-100 p-0 radius-12">
        {errorMessage && (
          <DengerAlert
            message={errorMessage}
            onClose={() => setErrorMessage("")}
          />
        )}

        <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
          <div className="d-flex align-items-center flex-wrap gap-3">
            <span className="text-md fw-medium text-secondary-light mb-0">
              Show
            </span>
            <select
              className="form-select form-select-sm w-auto ps-12 py-6"
              style={{
                fontSize: "16px",
                borderRadius: "7px",
                backgroundPositionX: "unset",
                backgroundPosition: "right 0.75rem center",
              }}
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
            >
              <option value="10">10 items per page</option>
              <option value="20">20 items per page</option>
              <option value="50">50 items per page</option>
              <option value="100">100 items per page</option>
            </select>

            <form
              className="navbar-search"
              style={{ position: "relative", minWidth: "280px" }}
              onSubmit={(e) => e.preventDefault()}
            >
              <input
                type="text"
                className="bg-base h-40-px pe-40"
                name="search"
                placeholder="Search by user name"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
                style={{
                  width: "100%",
                  minWidth: "280px",
                  paddingRight: searchQuery ? "5rem" : "2.5rem",
                }}
              />
              <Icon
                icon="ion:search-outline"
                className="icon"
                style={{
                  position: "absolute",
                  right: searchQuery ? "3.25rem" : "1rem",
                  top: "50%",
                  transform: "translateY(-50%)",
                  fontSize: "1.25rem",
                  color: "#999",
                  pointerEvents: "none",
                }}
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => {
                    setSearchQuery("");
                    setCurrentPage(1);
                  }}
                  style={{
                    position: "absolute",
                    right: "1rem",
                    top: "30%",
                    transform: "translateY(-50%)",
                    fontSize: "1.25rem",
                    color: "#000",
                    background: "transparent",
                    border: "none",
                    cursor: "pointer",
                  }}
                >
                  <Icon icon="ic:round-close" />
                </button>
              )}
            </form>

            <div style={{ width: "160px" }}>
              <DatePicker
                selected={startDate}
                onChange={(date) => {
                  setStartDate(date);
                  if (
                    date &&
                    endDate &&
                    moment(date).isAfter(moment(endDate), "day")
                  ) {
                    setEndDate(null);
                  }
                  setCurrentPage(1);
                }}
                preventOpenOnFocus={false}
                onKeyDown={(e) => e.preventDefault()}
                className="form-control no-caret"
                placeholderText="Start Date"
                dateFormat="yyyy-MM-dd"
                isClearable
              />
            </div>

            <div style={{ width: "160px" }}>
              <DatePicker
                selected={endDate}
                onChange={(date) => {
                  setEndDate(date);
                  setCurrentPage(1);
                }}
                minDate={startDate || undefined}
                preventOpenOnFocus={false}
                onKeyDown={(e) => e.preventDefault()}
                className="form-control no-caret"
                placeholderText="End Date"
                dateFormat="yyyy-MM-dd"
                isClearable
              />
            </div>
          </div>
        </div>

        <div className="card-body p-24">
          <div className="table-responsive scroll-sm">
            <table className="table bordered-table sm-table mb-0">
              <thead>
                <tr>
                  <th>#</th>
                  <th
                    onClick={() => handleSort("userName")}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="d-flex align-items-center gap-0">
                      User Name
                      {renderSortIcons("userName")}
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort("email")}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="d-flex align-items-center gap-0">
                      Email
                      {renderSortIcons("email")}
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort("joinDate")}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="d-flex align-items-center gap-0">
                      Join Date
                      {renderSortIcons("joinDate")}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={colSpan} className="text-center">
                      <span className="loader-15"></span>
                    </td>
                  </tr>
                ) : items.length > 0 ? (
                  items.map((item, index) => (
                    <tr key={item._id || index}>
                      <td>
                        {(currentPage - 1) * itemsPerPage + index + 1}
                      </td>
                      <td>
                        {item.userName || item.userDetail?.fullName || "N/A"}
                      </td>
                      <td>{item.userDetail?.email || item.email || "N/A"}</td>
                      <td>{formatDate(item.joinDate || item.createdAt)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={colSpan} className="text-center py-5">
                      No joined users found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-24">
            <span>
              {totalItems === 0 ? (
                <>No entry to show</>
              ) : (
                <>
                  Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                  {Math.min(currentPage * itemsPerPage, totalItems)} of{" "}
                  {totalItems} entries
                </>
              )}
            </span>

            <ul className="pagination d-flex flex-wrap align-items-center gap-2 justify-content-center mb-0">
              <li
                className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
              >
                <button
                  type="button"
                  disabled={currentPage === 1 || loading}
                  className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                  onClick={() =>
                    currentPage > 1 && setCurrentPage((prev) => prev - 1)
                  }
                >
                  <Icon icon="ep:d-arrow-left" />
                </button>
              </li>

              {getPaginationRange().map((pageNum) => (
                <li key={pageNum} className="page-item">
                  <button
                    type="button"
                    disabled={loading}
                    className={`page-link text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px text-md ${
                      currentPage === pageNum
                        ? "bg-primary-600 text-white"
                        : "bg-neutral-200"
                    }`}
                    onClick={() => setCurrentPage(pageNum)}
                  >
                    {pageNum}
                  </button>
                </li>
              ))}

              <li
                className={`page-item ${
                  currentPage === totalPages || totalPages === 0
                    ? "disabled"
                    : ""
                }`}
              >
                <button
                  type="button"
                  disabled={
                    currentPage === totalPages || totalPages === 0 || loading
                  }
                  className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                  onClick={() =>
                    currentPage < totalPages &&
                    setCurrentPage((prev) => prev + 1)
                  }
                >
                  <Icon icon="ep:d-arrow-right" />
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </MasterLayout>
  );
};

export default PromoterJoinedUsersList;
